// Legal / policy page content for House of Seya.
// Rendered by src/pages/Policy.jsx. Each section is a list of blocks:
//   { p }      paragraph
//   { h3 }     sub-heading
//   { ul }     bulleted list
//   { ol }     numbered list
//   { table }  { head: [...], rows: [[...], ...] }
//   { contact: true }  email + WhatsApp contact lines

export const POLICY_CONTACT = {
  brand: 'House of Seya',
  email: 'hello@houseofseya.com',
  whatsapp: '+918722447768'
};

const { email: EMAIL, whatsapp: WHATSAPP } = POLICY_CONTACT;

export const POLICIES = {
  'lifetime-buyback': {
    title: 'Buyback Terms of Service',
    eyebrow: 'Lifetime Buyback',
    intro: [
      'At House of Seya, we stand behind the quality and longevity of our jewelry. As part of our commitment to customer confidence, we offer a Lifetime Buyback Program on eligible products purchased directly from our official website.',
      'This program allows customers to return eligible products in exchange for store credit that can be used toward future purchases from House of Seya.',
      'By participating in this program, customers agree to the terms and conditions outlined below.'
    ],
    sections: [
      {
        heading: 'Eligibility & Overview',
        blocks: [
          { p: 'The Lifetime Buyback Program is available exclusively for products purchased directly from House of Seya through our official website.' },
          { p: 'To qualify for buyback:' },
          { ul: [
            'The product must be authentic and originally purchased from House of Seya.',
            'The product must pass our quality inspection process.',
            'The original invoice or proof of purchase must be provided.',
            'Any applicable product certificate and warranty card must be submitted, if originally provided.'
          ] },
          { p: 'The final determination regarding authenticity, condition, eligibility, valuation, deductions, and buyback approval shall rest solely with House of Seya and shall be final and binding.' }
        ]
      },
      {
        heading: 'Buyback Value',
        blocks: [
          { table: {
            head: ['Time Since Purchase', 'Buyback Value (% of original price)'],
            rows: [
              ['Within 12 months', '65%'],
              ['Between 12 – 24 months', '45%'],
              ['Beyond 24 months', '25%']
            ]
          } },
          { ul: [
            'Buyback value is calculated on the original purchase price (MRP).',
            'Taxes paid at the time of purchase are excluded from the buyback amount.',
            'Any discounts availed during the original purchase will be deducted from the buyback amount (specific to lab-grown diamonds).',
            'The buyback value will be issued in the form of store credit.',
            'Store credit is valid for 12 months from the date of issuance and can be redeemed exclusively on future purchases from House of Seya.'
          ] }
        ]
      },
      {
        heading: 'Condition Assessment',
        blocks: [
          { p: 'All products submitted for buyback undergo inspection by House of Seya.' },
          { p: 'Products showing excessive wear, structural damage, missing stones, damaged settings, broken chains, missing components, deep scratches, unauthorized modifications, or significant deterioration may:' },
          { ul: [
            'Receive a reduced buyback value.',
            'Be subject to repair deductions.',
            'Be deemed ineligible for buyback.'
          ] },
          { p: 'The buyback valuation determined after inspection shall be final.' }
        ]
      },
      {
        heading: 'Inspection & Documentation Requirements',
        blocks: [
          { p: 'All returned items will undergo a rigorous inspection to verify:' },
          { ul: [
            'Authenticity',
            'Condition',
            'Compliance with buyback terms'
          ] },
          { p: 'The following must be submitted with the product:' },
          { ul: [
            'Original product (unaltered and undamaged)',
            'Original Invoice / Copy',
            'Product Certificate',
            'Warranty Card',
            'Valid Government-issued Photo ID (of person requesting buyback)'
          ] },
          { p: 'Failure to provide sufficient documentation may result in delays or rejection of the request.' }
        ]
      },
      {
        heading: 'Shipping Instructions',
        blocks: [
          { ul: [
            'Customers are required to self-ship the product to our registered office in Mumbai, with packaging that ensures safe transit (e.g., bubble wrap, small box, or padded envelope).',
            'If House of Seya arranges pickup, logistics charges at actuals will be deducted from the buyback value.',
            'All products must be shipped using our designated partner: shiprocket',
            'Responsibility for the product remains with the customer until it is received and acknowledged by House of Seya.',
            'House of Seya shall not be liable for products lost, stolen, delayed, or damaged during transit to our facility.'
          ] }
        ]
      },
      {
        heading: 'Store Credit Terms',
        blocks: [
          { p: 'Approved buyback value will be issued exclusively as House of Seya Store Credit.' },
          { p: 'Store Credit:' },
          { ul: [
            'Is valid for 12 months from the date of issue.',
            'Is non-transferable.',
            'Cannot be redeemed for cash.',
            'Cannot be refunded or exchanged for cash.',
            'May only be used toward purchases made directly through House of Seya.',
            'Unused store credit will automatically expire after its validity period.'
          ] }
        ]
      },
      {
        heading: 'Request Process',
        blocks: [
          { p: 'To initiate a return under the Buyback Policy:' },
          { ol: [
            `Email our Customer Care at ${EMAIL} requesting buyback.`,
            `Call or WhatsApp us at ${WHATSAPP} to confirm the readiness of your package with all required documentation.`,
            'Our team will assist with shipping coordination if needed.',
            'Upon successful inspection and approval, store credit will be issued.'
          ] },
          { p: 'Store credit will be issued within 21 business days from the date of receiving and approving the product at our registered office.' }
        ]
      },
      {
        heading: 'Company Rights & Policy Changes',
        blocks: [
          { p: 'House of Seya reserves the right to:' },
          { ul: [
            'Refuse or approve buyback requests at its sole discretion.',
            'Modify or update the Buyback Terms without prior notice.'
          ] },
          { p: 'All updates will be posted on our official website and will take effect immediately. We encourage you to review these terms periodically for changes.' }
        ]
      },
      {
        heading: 'Need Assistance?',
        blocks: [
          { p: 'For any queries regarding our Buyback Policy, contact our support team via:' },
          { contact: true, whatsappLabel: 'Phone/WhatsApp' },
          { p: 'We’re here to help you enjoy a seamless experience with House of Seya.' }
        ]
      }
    ]
  },

  'return-refund-exchange': {
    title: 'Return, Refund & Exchange Policy',
    eyebrow: 'Returns & Refunds',
    intro: [
      'At House of Seya, every piece undergoes thorough quality checks and is securely packaged before dispatch. We are committed to delivering products that meet our quality standards and ensuring a smooth shopping experience for our customers.',
      'By placing an order on House of Seya, the customer acknowledges and agrees to the terms of this Return, Refund & Exchange Policy.',
      'Please read this policy carefully before making a purchase.'
    ],
    sections: [
      {
        heading: 'Return & Exchange Window',
        blocks: [
          { p: 'Customers may request an exchange or store credit within 7 days of delivery for eligible products.' },
          { p: 'Any claim relating to a damaged, defective, incorrect, missing, or incomplete order must be reported within 24 hours of delivery.' },
          { p: 'This policy applies exclusively to purchases made through our official website.' }
        ]
      },
      {
        heading: 'Eligibility',
        blocks: [
          { p: 'To be eligible for an exchange or store credit:' },
          { ul: [
            'The product must be unused, unworn, and in its original condition.',
            'All original packaging, certificates, tags, invoices, accessories, and complimentary gifts must be returned.',
            'The product must not show signs of wear, damage, resizing, repair, alteration, or misuse.',
            'All security seals and quality-control tags must remain intact.'
          ] },
          { p: 'All requests are subject to review and approval by House of Seya.' }
        ]
      },
      {
        heading: 'Required Documentation',
        blocks: [
          { p: 'To initiate any return, exchange, store credit, or refund request, customers must provide:' },
          { ul: [
            'A complete unboxing video recorded from before opening the package.',
            'Clear photographs of the product and packaging.',
            'Order ID and details of the issue.'
          ] },
          { p: 'Failure to provide sufficient proof may result in rejection of the request.' }
        ]
      },
      {
        heading: 'Security Seal Policy',
        blocks: [
          { p: 'All House of Seya orders are shipped with tamper-evident security seals.' },
          { p: 'Products returned with removed, damaged, broken, or tampered security seals may not be eligible for exchange, store credit, or refund unless the issue is reported immediately upon delivery and supported by valid proof.' }
        ]
      },
      {
        heading: 'Product Representation & Manufacturing Variations',
        blocks: [
          { p: 'Product photographs, renders, videos, and digital representations are intended for illustrative purposes only.' },
          { p: 'Minor variations in color, finish, dimensions, weight, stone placement, gemstone appearance, plating tone, or overall appearance resulting from photography, lighting, screen settings, hand-finishing, manufacturing processes, or natural product characteristics shall not be considered defects and shall not qualify for return, exchange, store credit, or refund.' }
        ]
      },
      {
        heading: 'Non-Returnable Items',
        blocks: [
          { p: 'The following items are not eligible for return, exchange, store credit, or refund:' },
          { ul: [
            'Customized, engraved, or personalized products.',
            'Made-to-order products once production has commenced.',
            'Earrings, for hygiene reasons, unless received damaged, defective, or incorrect.',
            'Products that have been worn, used, resized, repaired, altered, or damaged after delivery.',
            'Products returned without original packaging, certificates, tags, invoices, or security seals.',
            'Clearance, final-sale, promotional, discounted, or special-offer items.',
            'Complimentary gifts and promotional items.',
            'Orders refused after dispatch.',
            'Products showing evidence of misuse or improper handling.'
          ] }
        ]
      },
      {
        heading: 'Made-to-Order Products',
        blocks: [
          { p: 'Certain House of Seya products are handcrafted specifically upon order.' },
          { p: 'Once production has commenced, made-to-order products cannot be cancelled, exchanged, returned, or refunded except in cases of manufacturing defects, transit damage, or incorrect product delivery.' }
        ]
      },
      {
        heading: 'Exchange & Store Credit',
        blocks: [
          { p: 'Eligible products may be exchanged for another product of equal or higher value.' },
          { p: 'Any price difference must be paid by the customer before dispatch of the replacement product.' },
          { p: 'Where an exchange is not preferred or the requested product is unavailable, House of Seya may issue store credit valid for future purchases.' },
          { p: 'Exchanges are subject to product availability.' }
        ]
      },
      {
        heading: 'Refund Policy',
        blocks: [
          { p: 'Refunds are only offered under the following circumstances:' },
          { ul: [
            'A damaged product is received.',
            'A manufacturing defect is verified by our quality team.',
            'An incorrect product is delivered.',
            'The order cannot be fulfilled or delivered due to reasons attributable to House of Seya.'
          ] },
          { p: 'House of Seya reserves the sole discretion to determine whether an approved claim will be resolved through a repair, replacement, exchange, store credit, or refund.' },
          { p: 'Refunds will not be provided for change-of-mind purchases, sizing concerns, personal preferences, delayed deliveries caused by courier partners, or normal product characteristics.' }
        ]
      },
      {
        heading: 'Natural Wear & Care Exclusions',
        blocks: [
          { p: 'The following are not considered manufacturing defects:' },
          { ul: [
            'Surface scratches resulting from normal wear.',
            'Tarnishing or plating wear due to usage.',
            'Exposure to water, perfumes, cosmetics, chemicals, sweat, humidity, or moisture.',
            'Accidental damage or impact damage.',
            'Improper storage or handling.'
          ] },
          { p: 'Such conditions are not eligible for return, exchange, store credit, or refund.' }
        ]
      },
      {
        heading: 'Return Process',
        blocks: [
          { p: `To request an exchange, store credit, or refund, email us at ${EMAIL} with:` },
          { ul: [
            'Order ID',
            'Reason for request',
            'Unboxing video',
            'Supporting photographs'
          ] },
          { p: 'Our team will review the request and respond within 2–3 business days.' },
          { p: 'If approved, a Return Authorization will be issued.' }
        ]
      },
      {
        heading: 'Return Shipping',
        blocks: [
          { p: 'If the issue arises due to an incorrect, damaged, or defective product, House of Seya will bear the return shipping costs.' },
          { p: 'For all other approved returns or exchanges, return shipping costs shall be borne by the customer.' },
          { p: 'Products must be shipped using a trackable courier service. Responsibility for the returned shipment remains with the customer until the product is received and acknowledged by House of Seya.' }
        ]
      },
      {
        heading: 'Inspection & Approval',
        blocks: [
          { p: 'All returned products undergo inspection upon receipt.' },
          { p: "If a returned item fails inspection or does not meet the eligibility criteria outlined in this policy, House of Seya reserves the right to reject the request and return the product to the customer at the customer's expense." }
        ]
      },
      {
        heading: 'Refund Timelines',
        blocks: [
          { h3: 'Prepaid Orders' },
          { p: 'Approved refunds will be processed to the original payment method within 7 business days of approval.' },
          { h3: 'Cash on Delivery Orders' },
          { p: 'Approved refunds will be processed via bank transfer within 10–15 business days of approval.' },
          { p: 'Banks and payment providers may require additional time to complete processing.' },
          { p: 'Shipping charges, express delivery charges, gift packaging fees, and other service fees, if any, are non-refundable.' }
        ]
      },
      {
        heading: 'Fraud Prevention',
        blocks: [
          { p: 'House of Seya reserves the right to reject any request involving:' },
          { ul: [
            'Product substitution.',
            'Tampering.',
            'Fraudulent activity.',
            'Misrepresentation of product condition.',
            'Discrepancies between the returned item and the original order.'
          ] }
        ]
      },
      {
        heading: 'Delivery & Address Responsibility',
        blocks: [
          { p: 'Customers are responsible for providing accurate shipping information during checkout.' },
          { p: 'Orders marked as delivered by the courier partner to the shipping address provided at checkout shall be deemed successfully delivered.' },
          { p: 'House of Seya shall not be responsible for delays, losses, or additional charges arising from incorrect or incomplete delivery details provided by the customer.' }
        ]
      },
      {
        heading: 'Cash on Delivery Misuse',
        blocks: [
          { p: 'House of Seya reserves the right to restrict or refuse future Cash on Delivery orders from customers who repeatedly refuse deliveries or place fraudulent orders.' }
        ]
      },
      {
        heading: 'Lifetime Warranty & Buyback Program',
        blocks: [
          { p: 'The Lifetime Warranty and Lifetime Buyback Program are governed by separate terms and policies. Eligibility under those programs does not imply eligibility under this Return, Refund & Exchange Policy.' }
        ]
      },
      {
        heading: 'Limitation of Liability',
        blocks: [
          { p: "House of Seya's liability under this policy shall be limited to the purchase value of the product paid by the customer." }
        ]
      },
      {
        heading: 'Policy Updates',
        blocks: [
          { p: 'House of Seya reserves the right to amend, modify, or update this policy at any time without prior notice. The version published on the website at the time of purchase shall govern the order.' }
        ]
      },
      {
        heading: 'Contact Us',
        blocks: [
          { contact: true },
          { p: 'Our customer support team typically responds within 24 business hours.' }
        ]
      }
    ]
  },

  'shipping-policy': {
    title: 'Shipping Policy',
    eyebrow: 'Shipping & Delivery',
    intro: [
      'At House of Seya, every piece is crafted with care and shipped with the utmost attention to quality and security.'
    ],
    sections: [
      {
        heading: 'Processing & Dispatch',
        blocks: [
          { h3: 'Ready-to-Ship Products' },
          { p: 'Orders for in-stock products are processed and dispatched within 24–48 hours of order confirmation.' },
          { h3: 'Made-to-Order Products' },
          { p: 'Certain pieces are handcrafted and produced exclusively upon order. These products require approximately 3–4 weeks for manufacturing and quality checks before dispatch. The estimated production timeline will be mentioned on the respective product page wherever applicable.' }
        ]
      },
      {
        heading: 'Estimated Delivery Timelines',
        blocks: [
          { p: 'Once dispatched, delivery timelines are as follows:' },
          { ul: [
            'Metro Cities: 3–5 business days',
            'Rest of India: 5–7 business days'
          ] },
          { p: 'Deliveries to remote locations, border regions, Jammu & Kashmir, and select North-Eastern states may require additional transit time depending on courier service availability.' }
        ]
      },
      {
        heading: 'Order Tracking',
        blocks: [
          { p: 'You will receive your tracking details via email or SMS once your order has been packed and dispatched.' }
        ]
      },
      {
        heading: 'Weekend & Holiday Dispatches',
        blocks: [
          { p: 'Orders placed after 9:00 AM on Saturdays will be processed on the next working day. Our office remains closed on Sundays and public holidays.' }
        ]
      },
      {
        heading: 'Delivery Verification',
        blocks: [
          { p: 'To ensure secure delivery, our team may contact you via phone, email, or WhatsApp to confirm shipping details, particularly for Cash on Delivery (COD) orders.' }
        ]
      },
      {
        heading: 'Shipping Partners',
        blocks: [
          { p: 'We work with trusted logistics partners, including Shiprocket, Delhivery, Blue Dart, and other reputed courier services, to ensure safe and timely deliveries across India.' },
          { p: 'While we strive to dispatch and deliver all orders within the estimated timelines, delays caused by courier partners, weather conditions, regional restrictions, public holidays, or other unforeseen circumstances may occasionally occur. House of Seya shall not be held liable for delays arising from factors beyond our reasonable control.' }
        ]
      },
      {
        heading: 'Delivery Attempts',
        blocks: [
          { p: 'Our courier partners will make up to three delivery attempts. If a delivery remains unsuccessful or is refused, the package will be returned to us. Re-shipping charges may apply for a subsequent dispatch request.' }
        ]
      },
      {
        heading: 'Undeliverable Locations',
        blocks: [
          { p: 'In the rare event that delivery is not possible due to limitations in courier coverage, inaccessible delivery locations, or other circumstances beyond our control, we will notify you promptly. In such cases, you may choose to cancel your order and receive a full refund.' }
        ]
      },
      {
        heading: 'Damaged or Incorrect Orders',
        blocks: [
          { p: 'If your order arrives damaged or if you receive an incorrect item, please contact us within 24 hours of delivery with clear photographs of the package and product. Our team will review the request and arrange a replacement or refund, subject to verification and product availability.' }
        ]
      },
      {
        heading: 'Need Assistance?',
        blocks: [
          { p: "We're always happy to help." },
          { contact: true },
          { p: 'Our customer support team typically responds within 24 business hours.' }
        ]
      }
    ]
  },

  'privacy-policy': {
    title: 'Privacy Policy',
    eyebrow: 'Your Privacy',
    intro: [],
    sections: [
      {
        heading: 'Introduction',
        blocks: [
          { p: 'House of Seya respects your privacy. This Privacy Policy explains what personal information we collect when you visit our website, place an order, or get in touch with us, how we use it, and the choices available to you.' },
          { p: 'By using our website or sharing your information with us, you agree to the practices described in this Privacy Policy. If you do not agree with it, please do not use our website or share your information with us.' }
        ]
      },
      {
        heading: 'Information We Collect',
        blocks: [
          { p: 'Depending on how you interact with us, we may collect the following information:' },
          { ul: [
            'Your name.',
            'Your email address.',
            'Your phone number.',
            'Your shipping and billing address.',
            'Order information, such as the products you purchase or enquire about, order value, and order history.',
            'Account information, if you create or use an account with us.',
            'Communications and support information, such as messages, photographs, videos, and other details you share with us over email, phone, or WhatsApp.',
            'Device and technical information, where applicable, such as your browser type, device type, and how you interact with our website.'
          ] },
          { p: 'We collect most of this information directly from you, when you provide it to us while browsing, ordering, or contacting us.' }
        ]
      },
      {
        heading: 'How We Use Information',
        blocks: [
          { p: 'We use the information we collect to:' },
          { ul: [
            'Process, fulfil, and deliver your orders.',
            'Communicate with you about your orders, enquiries, and requests.',
            'Provide customer support, including for returns, exchanges, refunds, and buyback requests.',
            'Operate, maintain, and improve our website, products, and services.',
            'Detect and prevent fraud, misuse, or unauthorised activity.',
            'Comply with applicable laws and respond to lawful requests.'
          ] },
          { p: 'Where we send you promotional communications, you may opt out of them at any time by contacting us.' }
        ]
      },
      {
        heading: 'Order & Payment Information',
        blocks: [
          { p: 'When you place an order, we collect the details needed to confirm and fulfil it, such as your name, contact details, address, and the products ordered.' },
          { p: 'Where payments are processed through a third-party payment provider, your payment details are handled by that provider in accordance with its own terms and privacy policy. We may receive limited information about your payment, such as its status and amount, in order to confirm your order and process any refund.' }
        ]
      },
      {
        heading: 'Shipping & Delivery Information',
        blocks: [
          { p: 'To deliver your order, we share the information required for delivery — such as your name, shipping address, and phone number — with the logistics and courier partners handling your shipment.' },
          { p: 'We or our courier partners may contact you by phone, email, SMS, or WhatsApp to confirm shipping details or to share delivery and tracking updates.' }
        ]
      },
      {
        heading: 'Customer Support, WhatsApp & Email Communications',
        blocks: [
          { p: 'When you contact us by email, phone, or WhatsApp, we receive the information you choose to share with us, including your contact details and the content of your messages. We use this information to respond to you, resolve your request, and maintain a record of our communication.' },
          { p: 'Communications made through third-party platforms such as WhatsApp or your email provider are also subject to the terms and privacy policies of those platforms.' }
        ]
      },
      {
        heading: 'Cookies and Similar Technologies',
        blocks: [
          { p: 'Our website may use cookies and similar technologies, such as your browser’s local storage, to help the website function and to remember your preferences — for example, the items in your cart and the products you have recently viewed.' },
          { p: 'You can manage or clear cookies and site data through your browser settings. Please note that doing so may affect how some parts of the website work.' }
        ]
      },
      {
        heading: 'Data Sharing',
        blocks: [
          { p: 'We share personal information only where it is necessary, including with:' },
          { ul: [
            'Service providers who help us operate our website and business.',
            'Logistics and courier partners, to deliver your orders and handle returns.',
            'Payment providers, to process payments and refunds.',
            'Customer support and communication providers, to respond to your enquiries.',
            'Government, regulatory, or law enforcement authorities, where required by applicable law or to protect our legal rights.'
          ] },
          { p: 'These parties receive only the information they need to perform their services.' }
        ]
      },
      {
        heading: 'Data Security',
        blocks: [
          { p: 'We take reasonable measures to protect your personal information against unauthorised access, loss, misuse, or alteration.' },
          { p: 'However, no method of transmission over the internet or method of electronic storage is completely secure, and we cannot guarantee absolute security. Please take care when sharing personal information online.' }
        ]
      },
      {
        heading: 'Data Retention',
        blocks: [
          { p: 'We retain personal information for as long as it is needed for the purposes described in this Privacy Policy — such as fulfilling orders, providing support, and honouring our policies — and for as long as required to meet our legal, tax, and accounting obligations.' }
        ]
      },
      {
        heading: 'Your Rights & Choices',
        blocks: [
          { p: 'Subject to applicable law, you may contact us to:' },
          { ul: [
            'Request access to the personal information we hold about you.',
            'Request that we correct or update your personal information.',
            'Request that we delete your personal information, where we are not required to retain it.',
            'Withdraw your consent, where we rely on it, or opt out of promotional communications.',
            'Raise a concern about how your personal information is handled.'
          ] },
          { p: `To make a request, please write to us at ${EMAIL}. We may need to verify your identity before acting on your request.` }
        ]
      },
      {
        heading: 'Children’s Privacy',
        blocks: [
          { p: 'Our website is intended for use by adults. We do not knowingly collect personal information from children under the age of 18. If you believe a child has shared personal information with us, please contact us so that we can take appropriate steps.' }
        ]
      },
      {
        heading: 'Third-Party Links',
        blocks: [
          { p: 'Our website may contain links to third-party websites, platforms, and social media pages. We are not responsible for the content or privacy practices of those third parties, and we encourage you to review their privacy policies.' }
        ]
      },
      {
        heading: 'Policy Updates',
        blocks: [
          { p: 'We may update this Privacy Policy from time to time. The updated version will be published on this page, and we encourage you to review it periodically.' }
        ]
      },
      {
        heading: 'Contact Us',
        blocks: [
          { p: 'For any questions or requests regarding this Privacy Policy or your personal information, please contact:' },
          { p: 'House of Seya' },
          { contact: true }
        ]
      }
    ]
  }
};
